import classnames from "classnames";
import { isArray, isString } from "lodash-es";
import React from "react";

import { hotjarAttr, IHotjarProps, ITestable, testAttr } from "@/browser";
import { IAttachment } from "@/data/attachments";
import { BrandedLogo } from "@/views/components/Logo/BrandedLogo";
import { LazyLoadedLogo } from "@/views/components/Logo/LazyLoadedLogo";
import { LogoPlaceholder } from "@/views/components/Logo/LogoPlaceholder";

import { IStyleSize, IStyleTheme } from "../Style";

type TAvatarTheme = "primary" | "secondary";
type Styles = IStyleSize & IStyleTheme<TAvatarTheme>;

export interface IAvatarProps extends Partial<Styles>, IHotjarProps, ITestable {
  className?: string;
  imageUrl?: string;
  initials?: [string, string];
  round?: boolean;
  companyLogo?: IAttachment | null;
  onClick?: (event: React.MouseEvent<any>) => void;
}

export class Avatar extends React.PureComponent<IAvatarProps> {
  static defaultProps = {
    theme: "primary",
    size: "md",
  };

  render() {
    const baseClass = "el-avatar";
    const { theme, className, initials, size, round, onClick, imageUrl, companyLogo } = this.props;
    const companyLogoProps = { size: "xxs", className: "el-avatar__company-logo" };

    const classNames = classnames(
      baseClass,
      {
        [`el-avatar--size-${size}`]: !!size,
        [`el-avatar--theme-${theme}`]: !!theme,
        "el-avatar--round": !!round,
        "el-avatar--clickable": !!onClick,
      },
      className,
    );

    let label;

    if (imageUrl) {
      label = (
        <div
          className="el-avatar__image"
          style={{ backgroundImage: `url(${imageUrl})` }}
          {...testAttr(this.props, { sufix: "--avatar__image" })}
        />
      );
    } else {
      const letters = isArray(initials)
        ? initials
            .filter(i => isString(i))
            .map(s => s[0])
            .join("")
        : "";
      label = (
        <span className="el-avatar__initials" {...testAttr(this.props, { sufix: "--avatar__initials" })}>
          {letters}
        </span>
      );
    }

    return (
      <div
        className={classNames}
        onClick={onClick}
        {...hotjarAttr(this.props)}
        {...testAttr(this.props, { sufix: "--avatar" })}>
        {label}
        {companyLogo && (
          <LazyLoadedLogo logo={companyLogo}>
            {publicUrl => (
              <div className={`${baseClass}__company-border`}>
                <BrandedLogo
                  src={publicUrl}
                  orientation="portrait"
                  placeholder={<LogoPlaceholder size="sm" />}
                  {...companyLogoProps}
                />
              </div>
            )}
          </LazyLoadedLogo>
        )}
      </div>
    );
  }
}

export default Avatar;
